/**
 * Hook for Health Insurance Card field normalization and type-based control
 * Implements RA2 business rules: character normalization, auto-fill, field constraints
 */

import { useCallback, useEffect, useRef } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import {
  PATIENT_ADDRESS1_MAX_LENGTH,
  PATIENT_ADDRESS2_MAX_LENGTH,
  PATIENT_ADDRESS_KANA_MAX_LENGTH,
  PatientFormSchemaType,
} from '@pages/main/patient/types';
import {
  // normalizeInsurerNumber, // skipped: 保険者番号 is simple input only
  normalizeCardNumber,
  normalizePostalCode,
  normalizePhoneNumber,
} from '@utils/index';
import {
  useDefaultSeikatsuHogoInsurer,
  SEIKATSU_HOGO_INSURER_NUMBER,
} from '@pages/main/patient/hooks/useDefaultSeikatsuHogoInsurer';

function coerceInsuranceTypeId(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function toPatientCostRate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

const getPatientAddress2ForInsurance = (
  address2: unknown,
  address3: unknown,
  addressName: unknown
): string => {
  const parts = [address2, address3, addressName]
    .map(value => String(value ?? '').trim())
    .filter(Boolean);
  return parts.join(' ').slice(0, PATIENT_ADDRESS2_MAX_LENGTH);
};

interface UseHealthInsuranceNormalizationProps {
  watch: UseFormWatch<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  relationshipId: number | undefined;
}

export const useHealthInsuranceNormalization = ({
  watch,
  setValue,
  relationshipId,
}: UseHealthInsuranceNormalizationProps) => {
  // Watch fields
  const insurerNumber = watch('health_insurance_card.insurer_number');
  const cardMark = watch('health_insurance_card.card_mark');
  const cardNumber = watch('health_insurance_card.card_number');
  const insuranceTypeId = watch('health_insurance_card.insurance_type_id');
  /** Tracks prior 保険種別 so we only default 続柄→本人 when entering 後期高齢, not on every 続柄 change */
  const prevInsuranceTypeIdRef = useRef<number | undefined>(undefined);
  const postalCode1 = watch('health_insurance_card.postal_code1');
  const postalCode2 = watch('health_insurance_card.postal_code2');
  const telephone = watch('health_insurance_card.telephone');
  const pendingSeikatsuHogoAutofillRef = useRef(false);

  // Cached fetch of the 生活保護 insurer record so type-3 autofill uses the real
  // `insurer_name` / `insurer_kind_name` from the DB instead of hardcoded strings.
  const { data: seikatsuHogoInsurer } = useDefaultSeikatsuHogoInsurer();

  const resetInsuredPersonFromPatient = useCallback(() => {
    setValue('health_insurance_card.name', watch('patient_information.name') || '');
    setValue('health_insurance_card.name_kana', watch('patient_information.name_kana') || '');
    setValue('health_insurance_card.sex_id', watch('patient_information.sex_id') ?? null);
    setValue(
      'health_insurance_card.date_of_birth',
      watch('patient_information.date_of_birth') || ''
    );
    setValue('health_insurance_card.postal_code1', watch('patient_information.postal_code1') || '');
    setValue('health_insurance_card.postal_code2', watch('patient_information.postal_code2') || '');
    setValue(
      'health_insurance_card.address1',
      String(watch('patient_information.address1') || '').slice(0, PATIENT_ADDRESS1_MAX_LENGTH)
    );
    setValue(
      'health_insurance_card.address2',
      getPatientAddress2ForInsurance(
        watch('patient_information.address2'),
        watch('patient_information.address3'),
        watch('patient_information.address_name')
      )
    );
    setValue('health_insurance_card.telephone', watch('patient_information.telephone') || '');
    setValue(
      'health_insurance_card.address_kana',
      String(watch('patient_information.address_kana') || '').slice(
        0,
        PATIENT_ADDRESS_KANA_MAX_LENGTH
      )
    );
  }, [setValue, watch]);

  /**
   * Normalize insurer number with zero-padding (skipped: 保険者番号 is simple input only)
   */
  // useEffect(() => {
  //   if (insurerNumber !== undefined && insurerNumber !== null && insurerNumber !== '') {
  //     const normalized = normalizeInsurerNumber(insurerNumber);
  //     if (normalized !== insurerNumber) {
  //       setValue('health_insurance_card.insurer_number', normalized);
  //     }
  //   }
  // }, [insurerNumber]);

  /**
   * Normalize postal codes
   */
  useEffect(() => {
    if (postalCode1 !== undefined && postalCode1 !== null && postalCode1 !== '') {
      const normalized = normalizePostalCode(postalCode1);
      if (normalized !== postalCode1) {
        setValue('health_insurance_card.postal_code1', normalized);
      }
    }
  }, [postalCode1]);

  useEffect(() => {
    if (postalCode2 !== undefined && postalCode2 !== null && postalCode2 !== '') {
      const normalized = normalizePostalCode(postalCode2);
      if (normalized !== postalCode2) {
        setValue('health_insurance_card.postal_code2', normalized);
      }
    }
  }, [postalCode2]);

  /**
   * Normalize telephone
   */
  useEffect(() => {
    if (telephone !== undefined && telephone !== null && telephone !== '') {
      const normalized = normalizePhoneNumber(telephone);
      if (normalized !== telephone) {
        setValue('health_insurance_card.telephone', normalized);
      }
    }
  }, [telephone]);

  /**
   * Handle insurance type auto-fill and field controls
   */
  useEffect(() => {
    const prevType = prevInsuranceTypeIdRef.current;
    const typeId = coerceInsuranceTypeId(insuranceTypeId);

    // Clear auto-filled card_mark and card_number when switching away from 生活保護
    if (prevType === 3 && typeId !== 3) {
      setValue('health_insurance_card.card_mark', '');
      setValue('health_insurance_card.card_number', '');
    }

    if (typeId === 2) {
      pendingSeikatsuHogoAutofillRef.current = false;
      // 後期高齢: auto-fill insurer_kind_name
      setValue('health_insurance_card.insurer_kind_name', '後期高齢');
      // A2 edit.blade L973: 記号 is always cleared when 保険種別 = 後期高齢 (field becomes readonly).
      setValue('health_insurance_card.card_mark', '');
      // Clear patient cost and certificate when 後期高齢
      setValue('health_insurance_card.patient_cost_id', 0);
      setValue('health_insurance_card.elderly_insurance_certificate_id', 0);
      // Default 本人 only when switching into 後期高齢 from another種別 (submit validation still enforces 本人)
      if (prevType !== undefined && prevType !== 2) {
        setValue('health_insurance_card.relationship_id', 1);
        resetInsuredPersonFromPatient();
      }
    } else if (typeId === 3) {
      // 生活保護 auto-fill. The fixed insurer number `00000001` corresponds to a real
      // record in the `insurers` table; we use that record's actual `insurer_name`
      // and `insurer_kind_name` rather than hardcoded constants. If the lookup
      // hasn't resolved yet (or failed), leave those two fields untouched so we
      // don't clobber the saved values with stale hardcoded strings.
      const isUserSwitch = prevType !== undefined && prevType !== 3;
      if (isUserSwitch) {
        pendingSeikatsuHogoAutofillRef.current = true;
      }
      const shouldApplySeikatsuDefaults =
        isUserSwitch || pendingSeikatsuHogoAutofillRef.current;

      if (shouldApplySeikatsuDefaults && insurerNumber !== SEIKATSU_HOGO_INSURER_NUMBER) {
        setValue('health_insurance_card.insurer_number', SEIKATSU_HOGO_INSURER_NUMBER);
      }
      if (seikatsuHogoInsurer && shouldApplySeikatsuDefaults) {
        setValue('health_insurance_card.insurer_id', seikatsuHogoInsurer.id);
        setValue('health_insurance_card.insurer_name', seikatsuHogoInsurer.insurer_name);
        setValue(
          'health_insurance_card.insurer_kind_name',
          seikatsuHogoInsurer.insurer_kind_name
        );
        setValue(
          'health_insurance_card.claim_type_id',
          seikatsuHogoInsurer.medix_claim_type_id ?? null
        );
        setValue('health_insurance_card.cost_rate', seikatsuHogoInsurer.cost_rate);
        setValue('health_insurance_card.family_cost_rate', seikatsuHogoInsurer.family_cost_rate);

        const patientCostRate =
          toPatientCostRate(seikatsuHogoInsurer.patient_cost_rate) ??
          toPatientCostRate(
            relationshipId === 2
              ? seikatsuHogoInsurer.family_cost_rate
              : seikatsuHogoInsurer.cost_rate
          );

        if (patientCostRate !== null) {
          setValue('health_insurance_card.patient_cost_rate', patientCostRate);
        }

        pendingSeikatsuHogoAutofillRef.current = false;
      }
      // Only apply 生活保護 defaults during an actual type switch. On initial load
      // the form already contains the API snapshot; rewriting these values after
      // reset() causes useFormChangeGuard false positives when dev/stg insurer
      // master data differs from the saved patient row.
      if (shouldApplySeikatsuDefaults && cardMark !== '１') {
        setValue('health_insurance_card.card_mark', '１');
      }
      if (shouldApplySeikatsuDefaults && cardNumber !== '１') {
        setValue('health_insurance_card.card_number', '１');
      }
      if (shouldApplySeikatsuDefaults) {
        // Clear patient cost when 生活保護
        setValue('health_insurance_card.patient_cost_id', 0);
        setValue('health_insurance_card.elderly_insurance_certificate_id', 0);
      }
    } else {
      pendingSeikatsuHogoAutofillRef.current = false;
    }

    prevInsuranceTypeIdRef.current = typeId;
  }, [
    insuranceTypeId,
    insurerNumber,
    cardMark,
    cardNumber,
    relationshipId,
    seikatsuHogoInsurer,
    setValue,
    resetInsuredPersonFromPatient,
  ]);

  /**

   * Get field readonly state based on insurance type
   */
  const getFieldReadonly = useCallback(
    (fieldType: 'cardMark' | 'cardNumber'): boolean => {
      if (insuranceTypeId === 3) {
        // 生活保護: all fields readonly
        return true;
      }
      if (insuranceTypeId === 2) {
        // 後期高齢: card_mark readonly, others editable
        return fieldType === 'cardMark';
      }
      // 一般 and others: all editable
      return false;
    },
    [insuranceTypeId]
  );

  /**
   * Get field disabled state (for cost and certificate radios)
   */
  const getFieldDisabled = useCallback((): boolean => {
    if (insuranceTypeId === 2) {
      // 後期高齢: disable cost and certificate
      return true;
    }
    return false;
  }, [insuranceTypeId]);

  /**
   * Get card number label based on insurance type
   */
  const getCardNumberLabel = useCallback((): string => {
    if (insuranceTypeId === 2) {
      return '被保険者番号';
    }
    return '番号';
  }, [insuranceTypeId]);

  /** 記号 blur: 半角英数字・半角カナ → 全角（`normalizeCardNumber` と同様）。 */
  const handleCardMarkBlur = useCallback(() => {
    const currentValue = cardMark || '';
    if (currentValue) {
      const converted = normalizeCardNumber(currentValue);
      if (converted !== currentValue) {
        setValue('health_insurance_card.card_mark', converted);
      }
    }
  }, [cardMark, setValue]);

  /** 番号 blur: 半角英数字・半角カナ → 全角（`normalizeCardNumber`）。 */
  const handleCardNumberBlur = useCallback(() => {
    const currentValue = cardNumber || '';
    if (currentValue) {
      const converted = normalizeCardNumber(currentValue);
      if (converted !== currentValue) {
        setValue('health_insurance_card.card_number', converted);
      }
    }
  }, [cardNumber, setValue]);

  /**
   * Sync insurance name back to patient info
   * 本人 (1) / 67（本人）(3): full name + kana to patient
   * 家族 (2): replace surname segment only
   */
  const syncInsuranceNameToPatient = useCallback(
    (
      insuranceName: string,
      insuranceNameKana: string,
      patientName: string,
      patientNameKana: string
    ) => {
      if (relationshipId === 1 || relationshipId === 3) {
        if (insuranceName && insuranceName !== patientName) {
          setValue('patient_information.name', insuranceName);
        }
        if (insuranceNameKana && insuranceNameKana !== patientNameKana) {
          setValue('patient_information.name_kana', insuranceNameKana);
        }
      } else if (relationshipId === 2) {
        if (insuranceName && insuranceName.trim() !== '') {
          const patientNameParts = patientName.split(/[\s　]+/);
          patientNameParts[0] = insuranceName;
          const updatedName = patientNameParts.join(' ');
          setValue('patient_information.name', updatedName);
        }
        if (insuranceNameKana && insuranceNameKana.trim() !== '') {
          const patientNameKanaParts = patientNameKana.split(/[\s　]+/);
          patientNameKanaParts[0] = insuranceNameKana;
          const updatedNameKana = patientNameKanaParts.join(' ');
          setValue('patient_information.name_kana', updatedNameKana);
        }
      }
    },
    [setValue, relationshipId]
  );

  return {
    getFieldReadonly,
    getFieldDisabled,
    getCardNumberLabel,
    handleCardMarkBlur,
    handleCardNumberBlur,
    syncInsuranceNameToPatient,
    /** Copies the 受診者 into the 被保険者 column, as 本人 on entering 後期高齢 does. */
    resetInsuredPersonFromPatient,
  };
};
